import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { useIntl, useParams, history } from 'umi';
import { useMemoizedFn, useMount } from 'ahooks';
import { LANGUAGE_EN } from '@home/_config/config';
import Abnormal from '@home/components/Abnormal';
import { useTabTemplate } from '@/pages/aigc/pages/Agent/Home/hooks/useTabTemplate';
import SkillDetailHeader from './components/SkillDetailHeader';
import SkillDetailTabs from './components/SkillDetailTabs';
import SkillUsageSection from './components/SkillUsageSection';
import SkillLinkageSection from './components/SkillLinkageSection';
import SkillVersionSection from './components/SkillVersionSection';
import { useSkillsData } from '../hooks';
import { SKILL_DETAIL_TAB_CONFIG } from '../constants/skillDetail';
import { buildSkillDisplayViewModel, getSkillDisplayName } from '../utils/display';
import {
  navigateToHomeWithSkill,
  navigateToHomeWithSkillAndQuestion,
  replaceSkillDetail,
} from '../utils/navigation';
import { ESkillDetailTab } from '../types/skillDetail';
import type { ISkillData } from '../types';
import { buriedBtnPoint, buriedPointPage } from '@/pages/home/utils/buriedPoint';
import './index.scss';

/**
 * Skill 详情页 Container（Layer 2）
 *
 * 职责：
 * - 通过 `useSkillsData`（Layer 3）获取全量 Skills 数据
 * - 通过 `buildSkillDisplayViewModel`（Layer 2 工具函数）计算展示视图模型
 * - 编排子组件（Header / Tabs / Usage / Linkage / Version）的渲染
 * - 处理导航副作用（跳转首页对话、切换详情、推荐点击）
 *
 * 子组件均为 Layer 1 展示组件，不直接调用导航 / API，统一由本 Container 通过回调下发。
 */
const SkillDetailPage: FC = () => {
  const { formatMessage, locale } = useIntl();
  const { skillId = '' } = useParams<{ skillId: string; }>();

  const isEN = locale === LANGUAGE_EN;

  // --- Layer 3：数据获取 ---
  const {
    allSkills,
    loading,
    handleDownloadSkill,
    handleInstallSkill,
    isSkillDownloading,
  } = useSkillsData(isEN);

  // --- Layer 2：数据变换 ---
  /** 从全量列表中匹配当前 Skill */
  const skill = useMemo(
    () => allSkills.find((item) => item.skill_id === skillId),
    [allSkills, skillId],
  );

  /** 构建展示视图模型（displayName / description / author 等） */
  const display = useMemo(
    () => (skill ? buildSkillDisplayViewModel(skill, isEN) : null),
    [skill, isEN],
  );

  /** Skill 不存在且非加载中 → 展示空态 */
  const notFound = Boolean(skillId) && !loading && !skill;

  // --- 页面曝光打点 ---
  useMount(() => {
    buriedPointPage({
      pageId: '700501',
      pageName: 'skillDetail',
      extInfo: skillId,
      pagePath: '/agent/skill-detail',
      typeId: 'aigc',
    });
  });

  // --- Tab 状态管理 ---
  const tabs = useMemo(
    () => SKILL_DETAIL_TAB_CONFIG.map((tab) => ({
      id: tab.id,
      label: tab.labelKey,
    })),
    [],
  );

  const {
    activeId: activeTab,
    onTabChange,
  } = useTabTemplate({
    tabs,
    defaultActiveId: ESkillDetailTab.USAGE,
    // storageKey: `__dba_skill_detail_tab__${skillId}`,
  });

  /** 锁定外层滚动容器，由 tabs panel 统一滚动 */
  useEffect(() => {
    const contentBody = document.querySelector<HTMLElement>(
      '.dba-agent-layout-body .tea-layout__content-body',
    );
    if (!contentBody) {
      return undefined;
    }

    contentBody.classList.add('dba-skill-detail-scroll-lock');

    return () => {
      contentBody.classList.remove('dba-skill-detail-scroll-lock');
    };
  }, []);

  // --- 导航副作用回调（Layer 2 编排） ---

  /** 点击"立即对话"→ 跳转 Agent 首页并携带当前 Skill */
  const handleChatNow = useCallback(() => {
    if (!skill) {
      return;
    }
    buriedBtnPoint({
      pageId: '700501',
      buttonId: '70090110',
      extInfo: JSON.stringify({ skill_id: skill.skill_id, skill_name: getSkillDisplayName(skill, isEN) }),
    });
    navigateToHomeWithSkill(skill, isEN);
  }, [skill, isEN]);

  /** 下载 Skill 包（包装为 void 以消除 floating promise 警告） */
  const handleDownload = useCallback(
    (targetSkill: ISkillData) => {
      void handleDownloadSkill(targetSkill);
    },
    [handleDownloadSkill],
  );

  /** 安装 Skill 到 DataBrain（包装为 void 以消除 floating promise 警告） */
  const handleInstall = useCallback(
    (targetSkillId: string) => {
      void handleInstallSkill(targetSkillId);
    },
    [handleInstallSkill],
  );

  /** 推荐列表点击 → 替换当前详情页 Skill（不产生历史栈） */
  const handleRecommendSkillClick = useMemoizedFn((nextSkillId: string) => {
    if (!nextSkillId || nextSkillId === skillId) {
      return;
    }
    buriedBtnPoint({
      pageId: '700501',
      buttonId: '70090112',
      extInfo: JSON.stringify({
        skill_id: nextSkillId,
        current_skill_id: skillId,
        skill_name: skill ? getSkillDisplayName(skill, isEN) : '',
      }),
    });
    replaceSkillDetail(nextSkillId);
  });

  /** 返回上一级路由 */
  const handleBack = useMemoizedFn(() => {
    history.goBack();
  });

  /** 联动案例"试一下"→ 跳转首页并预填 Skill + 提问文案 */
  const handleTryLinkage = useMemoizedFn((question: string) => {
    if (!skill) {
      return;
    }
    buriedBtnPoint({
      pageId: '700501',
      buttonId: '70090113',
      extInfo: JSON.stringify({
        skill_id: skill.skill_id,
        skill_name: getSkillDisplayName(skill, isEN),
      }),
    });
    navigateToHomeWithSkillAndQuestion(skill, question, isEN);
  });

  // --- Tab 内容编排 ---
  const detailTabs = useMemo(() => {
    if (!skill || !display) {
      return [];
    }

    return [
      {
        id: ESkillDetailTab.USAGE,
        component: (
          <SkillUsageSection
            display={display}
            skill={skill}
            allSkills={allSkills}
            isEN={isEN}
            onRecommendSkillClick={handleRecommendSkillClick}
          />
        ),
      },
      {
        id: ESkillDetailTab.LINKAGE,
        component: (
          <SkillLinkageSection
            skill={skill}
            isEN={isEN}
            enabled
            onTryLinkage={handleTryLinkage}
          />
        ),
      },
      {
        id: ESkillDetailTab.VERSION,
        component: (
          <SkillVersionSection
            skillId={skill.skill_id}
            enabled
          />
        ),
      },
    ];
  }, [
    skill,
    display,
    allSkills,
    isEN,
    handleRecommendSkillClick,
    handleTryLinkage,
  ]);

  // --- 渲染 ---
  return (
    <div className="dba-skill-detail">
      <Abnormal
        isLoading={loading}
        isNoData={notFound}
        isError={false}
        nodataMsg={formatMessage({ id: 'common_nodata' })}
        classNames="dba-skill-detail__abnormal"
        style={{ height: '100%' }}
        component={
          skill && display ? (
            <div className="dba-skill-detail__card">
              <SkillDetailHeader
                display={display}
                skill={skill}
                activeTab={activeTab as ESkillDetailTab}
                onTabChange={onTabChange}
                onChatNow={handleChatNow}
                onInstall={handleInstall}
                onDownload={handleDownload}
                onBack={handleBack}
                isEN={isEN}
                isDownloading={isSkillDownloading(skill.skill_id)}
              />
              <SkillDetailTabs
                activeTab={activeTab as ESkillDetailTab}
                tabs={detailTabs}
              />
            </div>
          ) : (
            <></>
          )
        }
      />
    </div>
  );
};

export default SkillDetailPage;
